// ManualPlateEntry.tsx
import React, { useEffect, useRef, useState } from "react";
import { getAuthHeaders } from "../lib/api";


interface Props {
  onClose?: () => void;
  onSubmitted?: (result: any) => void;

  apiUrl?: string; // default API endpoint
  tenantHint?: string;
}

const defaultApi = "/api/kanban/manual-entry";

function normalizePlateDisplay(s: string) {
  return (s || "").toUpperCase().replace(/[\s\-_]+/g, " ").trim();
}
function normalizePlateStored(s: string) {
  return (s || "").toUpperCase().replace(/[\s\-_]+/g, "").trim();
}

export function ManualPlateEntry({ onClose, onSubmitted, apiUrl = defaultApi, tenantHint }: Props) {
  const [plate, setPlate] = useState<string>("");
  const [direction, setDirection] = useState<"entry" | "exit">("entry");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // simple configurable validation; you can swap with tenant-specific regex
  const plateRegex = /^[A-Z0-9\s\-]{2,12}$/;

  useEffect(() => {
    // autofocus on open
    inputRef.current?.focus();
  }, []);



  // Keyboard: Enter submits, Escape closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter" && !loading) handleSubmit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [plate, loading]);

  async function startCapture() {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      setStream(s);
      setCapturing(true);
      if (videoRef.current) videoRef.current.srcObject = s;
      videoRef.current?.play();
    } catch (err: any) {
      setError("Cannot access camera: " + (err?.message || err));
    }
  }
  function stopCapture() {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
    }
    setStream(null);
    setCapturing(false);
  }
  function snapshot() {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(blob => {
      if (!blob) return;
      const f = new File([blob], `plate-${Date.now()}.jpg`, { type: "image/jpeg" });
      setFile(f);
      stopCapture();
    }, "image/jpeg", 0.85);
  }


  function validatePlate(p: string) {
    const norm = normalizePlateDisplay(p);
    if (!norm) return "Enter a plate number";
    if (!plateRegex.test(norm)) return "Invalid plate format (letters, digits, spaces and - only)";
    return null;
  }

  async function handleSubmit() {
    const v = validatePlate(plate);
    if (v) { setError(v); return; }
    setError(null);
    setLoading(true);

    const payload = {
      plateNumber: normalizePlateStored(plate),
      rawPlate: normalizePlateDisplay(plate),
      direction
    };


    try {
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status} ${text}`);
      }
      const body = await res.json();
      onSubmitted?.(body);
      setLoading(false);
      onClose?.();
    } catch (err: any) {
      setLoading(false);
      setError(err?.message || "Network error");
    }
  }

  return (
    <div className="p-4 bg-white rounded shadow-md w-full max-w-md" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold">Manual plate entry</h3>
        <button className="text-sm text-gray-500" onClick={() => onClose?.()}>Close</button>
      </div>

      <label className="block text-sm font-medium text-gray-700">Plate number</label>
      <input
        ref={inputRef}
        value={plate}
        onChange={(e) => setPlate(e.target.value.toUpperCase())}
        placeholder="e.g. BW86 SWZN"
        className="mt-1 block w-full p-2 border rounded focus:outline-none focus:ring"
        aria-invalid={!!error}
      />
      {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
      <label className="block text-sm font-medium text-gray-700 mt-3">
        Direction
      </label>
      <div className="flex gap-4 mt-1">
        {["entry", "exit"].map(opt => (
          <label key={opt} className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name="direction"
              value={opt}
              checked={direction === opt}
              onChange={(e) => setDirection(e.target.value as "entry" | "exit")}
              className="text-blue-600 focus:ring-blue-600"
            />
            <span className="capitalize">{opt}</span>
          </label>
        ))}
      </div>
      <div className="mt-2">
        <div className="flex gap-2">
          <button
            onClick={() => handleSubmit()}
            disabled={loading}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-60"
          >
            {loading ? "Submitting…" : "Submit"}
          </button>

          {/* <label className="px-3 py-1 bg-gray-100 border rounded cursor-pointer">
            <input type="file" accept="image/*" className="sr-only" onChange={(e)=> {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
            }} />
            Attach photo
          </label> */}

          {/* {!capturing && (
            <button onClick={startCapture} className="px-3 py-1 bg-green-600 text-white rounded">Use camera</button>
          )}
          {capturing && (
            <button onClick={snapshot} className="px-3 py-1 bg-yellow-500 rounded">Snapshot</button>
          )} */}
        </div>

        {file && <p className="mt-2 text-sm">Attached: {file.name}</p>}
      </div>



      {/* Camera preview */}
      {capturing && (
        <div className="mt-3">
          <video ref={videoRef} className="w-full rounded" style={{ maxHeight: 240 }} playsInline />
          <canvas ref={canvasRef} className="hidden" />
          <div className="mt-1 flex gap-2">
            <button onClick={snapshot} className="px-3 py-1 bg-yellow-500 rounded">Capture</button>
            <button onClick={stopCapture} className="px-3 py-1 bg-gray-200 rounded">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
